import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  Grid,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { AssessmentRounded as LedgerIcon } from "@mui/icons-material";
import axios from "axios";
import { AutoTaskAuthContext } from "../../../Context/AutoTaskContext";
import { useAccountsNav } from "../../../utils/moduleNav";
import { usePawnLedgerScope } from "../pawnLedgerScope";
import { reportPageSx, reportTableSx } from "../../PawnShop/reportLayout";
import ReportDateWindow from "../../PawnShop/reportDateWindow";
import { DEFAULT_SORT, sortRows } from "../../PawnShop/reportSort";
import {
  BLUE_PALE,
  EmptyState,
  FilterActions,
  FilterCard,
  INK,
  INK_MUTED,
  LoadingPanel,
  ResultsHeader,
  SUCCESS,
  TODAY,
  cellSx,
  fmtAmt,
  fmtAmtAlways,
  fmtBalance,
  fmtDate,
  footCellSx,
  monthStart,
  headCellSx,
  pdfTableTheme,
  rangeLabel,
  resultsCardSx,
  startPdf,
} from "./ledgerReportKit";

const API = process.env.REACT_APP_API_BASE_URL;

// Rows are ledger transactions, so the register sorts on what a transaction
// has. Running balance is deliberately NOT offered: it is derived from the row
// order, so reordering the rows would print a balance column that no longer
// adds up.
const SORT_OPTIONS = [
  { key: "date", label: "Date" },
  { key: "voucher", label: "Voucher No" },
  { key: "debit", label: "Debit" },
  { key: "credit", label: "Credit" },
];

/**
 * One account's statement for a date window: opening balance, every voucher
 * that touched it, closing balance.
 *
 * The Travel ERP version of this screen also filtered by Party and by
 * sub-account (cost centre). Neither exists in the pawn books — every pawn
 * posting leaves `party_account`, `party_code` and `costcentreid` null — so in
 * this module the report is just "pick an account, pick the days".
 */
const PawnIndividualLedger = ({ embedded = false }) => {
  const { userInfo, currentStoreInfo, defaultFY, defaultFYId } =
    useContext(AutoTaskAuthContext) || {};
  const storeId = userInfo?.store_id;
  const { go: goAccounts } = useAccountsNav();
  const { pawnMode, scope: pawnScope } = usePawnLedgerScope();

  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);

  // The window defaults to the month to date — a ledger is read as a run of
  // days, not as one day of it.
  const [dateMode, setDateMode] = useState("range");
  const [singleDate, setSingleDate] = useState(TODAY());
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(TODAY());

  const [sort, setSort] = useState(DEFAULT_SORT);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  const showError = useCallback(
    (message) => setSnackbar({ open: true, message }),
    [],
  );

  const effFrom = dateMode === "single" ? singleDate : fromDate;
  const effTo = dateMode === "single" ? singleDate : toDate;

  // ── Accounts for the picker ───────────────────────────────────────────────
  useEffect(() => {
    if (storeId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/accounts/accounts/${storeId}/accounts-only`,
        );
        const list = Array.isArray(data?.data) ? data.data : [];
        if (cancelled) return;
        setAccounts(list);
        // Land on the store's cash account, which is what a clerk opens this
        // screen for nine times in ten.
        setAccount(
          (prev) =>
            prev ||
            list.find((a) => /^cash$/i.test(a.account_name || "")) ||
            list[0] ||
            null,
        );
      } catch {
        if (!cancelled) showError("Could not load the account list.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeId, showError]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const buildReport = useCallback(async () => {
    if (storeId == null || !account || !defaultFYId) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/accounts/individual-ledger`, {
        store_id: parseInt(storeId, 10),
        filter_type: "Account",
        account_id: account.account_id,
        fy_id: defaultFYId,
        from_date: effFrom,
        to_date: effTo,
        ...pawnScope,
      });
      if (data?.success) setReport(data.data);
      else {
        setReport(null);
        showError(data?.message || "Failed to generate the ledger statement.");
      }
    } catch (err) {
      setReport(null);
      showError(
        err?.response?.data?.message || "Failed to generate the ledger statement.",
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, account, defaultFYId, effFrom, effTo, pawnMode, showError]);

  // Every filter here is a picker, so each one auto-loads and the View button
  // is a manual refresh (feedback_report_auto_fetch_on_date).
  useEffect(() => {
    buildReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, pawnMode, account, dateMode, singleDate, fromDate, toDate]);

  const handleReset = () => {
    setDateMode("range");
    setSingleDate(TODAY());
    setFromDate(monthStart());
    setToDate(TODAY());
    setSort(DEFAULT_SORT);
  };

  // ── Rows ──────────────────────────────────────────────────────────────────
  // Sorting is applied LAST and only to the transaction rows; the opening and
  // closing balances are not transactions and stay pinned where they belong.
  const rows = useMemo(
    () =>
      sortRows(report?.transactions || [], sort, {
        date: (r) => r.date,
        voucher: (r) => r.voucher_number,
        debit: (r) => parseFloat(r.debit_amount) || 0,
        credit: (r) => parseFloat(r.credit_amount) || 0,
      }),
    [report, sort],
  );

  // A running balance only means anything in the order the register was built,
  // so the column is dropped the moment the rows are reordered.
  const showBalance = !sort.key;

  const openVoucher = (voucherNo) => {
    if (!voucherNo) return;
    // ledgerType rides along so the entry screen's Back lands on THIS chip
    // rather than on the container's default one.
    goAccounts("/accounts-entry", {
      state: { voucher_no: voucherNo, fromLedger: true, ledgerType: "ledger" },
    });
  };

  // ── PDF ───────────────────────────────────────────────────────────────────
  const generatePDF = () => {
    if (!report) return;
    const { doc, margin, startY } = startPdf({
      store: currentStoreInfo,
      title: `Ledger Statement — ${report.account_details?.account_name || ""}`,
      context: [
        rangeLabel(effFrom, effTo),
        defaultFY ? `FY ${defaultFY}-${defaultFY + 1}` : null,
      ]
        .filter(Boolean)
        .join("  |  "),
    });

    const body = [
      [
        { content: "Opening Balance", colSpan: 4, styles: { fontStyle: "bold" } },
        { content: "", styles: {} },
        { content: "", styles: {} },
        {
          content: fmtBalance(report.opening_balance, report.opening_balance_type),
          styles: { fontStyle: "bold", halign: "right" },
        },
      ],
      ...rows.map((r) => [
        fmtDate(r.date),
        r.voucher_number || "—",
        r.voucher_type || "",
        r.narration || "",
        { content: fmtAmt(r.debit_amount), styles: { halign: "right" } },
        { content: fmtAmt(r.credit_amount), styles: { halign: "right" } },
        {
          content: showBalance
            ? fmtBalance(r.running_balance, r.balance_type)
            : "",
          styles: { halign: "right" },
        },
      ]),
    ];

    doc.autoTable({
      startY,
      head: [
        ["Date", "Voucher No", "Type", "Narration", "Debit", "Credit", "Balance"],
      ],
      body,
      foot: [
        [
          { content: "Totals", colSpan: 4, styles: { halign: "left" } },
          { content: fmtAmtAlways(report.total_debit), styles: { halign: "right" } },
          { content: fmtAmtAlways(report.total_credit), styles: { halign: "right" } },
          {
            content: fmtBalance(
              report.closing_balance,
              report.closing_balance_type,
            ),
            styles: { halign: "right" },
          },
        ],
      ],
      footStyles: {
        fillColor: [248, 250, 252],
        textColor: [15, 23, 42],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 26 },
        2: { cellWidth: 24 },
        4: { halign: "right", cellWidth: 30 },
        5: { halign: "right", cellWidth: 30 },
        6: { halign: "right", cellWidth: 38 },
      },
      ...pdfTableTheme(margin),
    });

    doc.save(
      `Ledger_${(report.account_details?.account_name || "account").replace(
        /[^a-zA-Z0-9]/g,
        "_",
      )}_${effFrom}_to_${effTo}.pdf`,
    );
  };

  return (
    <Box sx={reportPageSx(embedded)}>
      <FilterCard>
        <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size="small"
              options={accounts}
              value={account}
              onChange={(_e, v) => setAccount(v)}
              getOptionLabel={(o) =>
                o?.account_name
                  ? `${o.account_name}${o.account_code ? ` — ${o.account_code}` : ""}`
                  : ""
              }
              isOptionEqualToValue={(o, v) =>
                String(o.account_id) === String(v?.account_id)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="A/C"
                  placeholder="Select an account"
                  // Mandatory: outline it rather than let the request fail.
                  error={!account}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <ReportDateWindow
            label="Period"
            dateMode={dateMode}
            setDateMode={setDateMode}
            singleDate={singleDate}
            setSingleDate={setSingleDate}
            fromDate={fromDate}
            setFromDate={setFromDate}
            toDate={toDate}
            setToDate={setToDate}
          />

          <Grid item xs={12} md="auto">
            <FilterActions
              onView={buildReport}
              onReset={handleReset}
              loading={loading}
              sortOptions={SORT_OPTIONS}
              sort={sort}
              onSortChange={setSort}
            />
          </Grid>
        </Grid>
      </FilterCard>

      <Card elevation={0} sx={resultsCardSx}>
        <ResultsHeader
          title={`LEDGER STATEMENT — ${rangeLabel(effFrom, effTo)}`}
          subtitle={[
            report?.account_details?.account_name || account?.account_name || "—",
            report
              ? `Opening ${fmtBalance(
                  report.opening_balance,
                  report.opening_balance_type,
                )}`
              : null,
          ]
            .filter(Boolean)
            .join("  ·  ")}
          count={
            report
              ? `${report.transaction_count} ${
                  report.transaction_count === 1 ? "entry" : "entries"
                }`
              : null
          }
          onPdf={generatePDF}
          pdfDisabled={loading || !report}
        />

        {loading ? (
          <LoadingPanel />
        ) : !account ? (
          <EmptyState icon={<LedgerIcon />}>
            Pick an account in the A/C box above to read its ledger.
          </EmptyState>
        ) : !report || rows.length === 0 ? (
          <EmptyState icon={<LedgerIcon />}>
            No entries on {account.account_name} for {rangeLabel(effFrom, effTo)}.
          </EmptyState>
        ) : (
          <TableContainer sx={reportTableSx}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 112 }}>Date</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 110 }}>
                    Voucher No
                  </TableCell>
                  <TableCell sx={{ ...headCellSx, width: 100 }}>Type</TableCell>
                  <TableCell sx={headCellSx}>Narration</TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 130 }}>
                    Debit
                  </TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 130 }}>
                    Credit
                  </TableCell>
                  {showBalance && (
                    <TableCell align="right" sx={{ ...headCellSx, width: 150 }}>
                      Balance
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* Opening balance leads the register, as it does in print. */}
                <TableRow>
                  <TableCell
                    colSpan={showBalance ? 6 : 6}
                    sx={{ ...cellSx, fontWeight: 700, color: INK, bgcolor: "#F8FAFC" }}
                  >
                    Opening Balance
                  </TableCell>
                  {showBalance && (
                    <TableCell
                      align="right"
                      sx={{
                        ...cellSx,
                        fontWeight: 700,
                        color: INK,
                        bgcolor: "#F8FAFC",
                      }}
                    >
                      {fmtBalance(
                        report.opening_balance,
                        report.opening_balance_type,
                      )}
                    </TableCell>
                  )}
                </TableRow>

                {rows.map((r) => (
                  <Tooltip
                    key={r.lkey}
                    title="Double-click to open this voucher"
                    followCursor
                  >
                    <TableRow
                      hover
                      onDoubleClick={() => openVoucher(r.voucher_number)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                        {fmtDate(r.date)}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: 700, color: INK }}>
                        {r.voucher_number || "—"}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, color: INK_MUTED }}>
                        {r.voucher_type || ""}
                      </TableCell>
                      <TableCell sx={cellSx}>{r.narration || ""}</TableCell>
                      <TableCell align="right" sx={cellSx}>
                        {fmtAmt(r.debit_amount)}
                      </TableCell>
                      <TableCell align="right" sx={cellSx}>
                        {fmtAmt(r.credit_amount)}
                      </TableCell>
                      {showBalance && (
                        <TableCell
                          align="right"
                          sx={{ ...cellSx, color: INK, fontWeight: 600 }}
                        >
                          {fmtBalance(r.running_balance, r.balance_type)}
                        </TableCell>
                      )}
                    </TableRow>
                  </Tooltip>
                ))}

                {/* Closing balance closes it, again as in print. */}
                <TableRow sx={{ bgcolor: BLUE_PALE }}>
                  <TableCell
                    colSpan={6}
                    sx={{ ...cellSx, fontWeight: 700, color: INK, borderBottom: "none" }}
                  >
                    Closing Balance
                  </TableCell>
                  {showBalance && (
                    <TableCell
                      align="right"
                      sx={{
                        ...cellSx,
                        fontWeight: 800,
                        color: INK,
                        borderBottom: "none",
                      }}
                    >
                      {fmtBalance(
                        report.closing_balance,
                        report.closing_balance_type,
                      )}
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>

              <TableFooter>
                <TableRow>
                  <TableCell colSpan={4} sx={footCellSx}>
                    Totals
                  </TableCell>
                  <TableCell align="right" sx={{ ...footCellSx, color: SUCCESS }}>
                    {fmtAmtAlways(report.total_debit)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...footCellSx, color: SUCCESS }}>
                    {fmtAmtAlways(report.total_credit)}
                  </TableCell>
                  {showBalance && (
                    <TableCell align="right" sx={footCellSx}>
                      {fmtBalance(
                        report.closing_balance,
                        report.closing_balance_type,
                      )}
                    </TableCell>
                  )}
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PawnIndividualLedger;
